import { lazy } from "react";
import type { RouteObject } from "react-router";

// ---------------------------------------------------------------------------
// Accounting module routes — lazy-loaded from existing Next.js page files
// ---------------------------------------------------------------------------

const AccountingIndex = lazy(() => import("./pages/page"));
const Dashboard = lazy(() => import("./pages/dashboard/page"));

// Invoices
const Invoices = lazy(() => import("./pages/invoices/page"));
const InvoiceNew = lazy(() => import("./pages/invoices/new/page"));
const InvoiceDetail = lazy(() => import("./pages/invoices/[id]/page"));

// Payments
const Payments = lazy(() => import("./pages/payments/page"));
const PaymentDetail = lazy(() => import("./pages/payments/[id]/page"));
const BankAccounts = lazy(() => import("./pages/bank-accounts/page"));
const BankStatements = lazy(() => import("./pages/bank-statements/page"));
const Cheques = lazy(() => import("./pages/cheques/page"));
const ChequeDetail = lazy(() => import("./pages/cheques/[id]/page"));

// Receipts
const Receipts = lazy(() => import("./pages/receipts/page"));
const ReceiptDetail = lazy(() => import("./pages/receipts/[id]/page"));

// Journal Entries
const JournalEntries = lazy(() => import("./pages/journal-entries/page"));
const JournalEntryNew = lazy(() => import("./pages/journal-entries/new/page"));
const JournalEntryDetail = lazy(() => import("./pages/journal-entries/[id]/page"));

// Credit Notes
const CreditNotes = lazy(() => import("./pages/credit-notes/page"));
const CreditNoteNew = lazy(() => import("./pages/credit-notes/new/page"));
const CreditNoteDetail = lazy(() => import("./pages/credit-notes/[id]/page"));

// Debit Notes
const DebitNotes = lazy(() => import("./pages/debit-notes/page"));
const DebitNoteNew = lazy(() => import("./pages/debit-notes/new/page"));
const DebitNoteDetail = lazy(() => import("./pages/debit-notes/[id]/page"));

// Fixed Assets
const FixedAssets = lazy(() => import("./pages/fixed-assets/page"));
const FixedAssetNew = lazy(() => import("./pages/fixed-assets/new/page"));
const FixedAssetDetail = lazy(() => import("./pages/fixed-assets/[id]/page"));

// Budgets
const Budgets = lazy(() => import("./pages/budgets/page"));
const BudgetNew = lazy(() => import("./pages/budgets/new/page"));
const BudgetDetail = lazy(() => import("./pages/budgets/[id]/page"));

// Recurring Templates
const RecurringTemplates = lazy(() => import("./pages/recurring-templates/page"));
const RecurringTemplateNew = lazy(() => import("./pages/recurring-templates/new/page"));
const RecurringTemplateDetail = lazy(() => import("./pages/recurring-templates/[id]/page"));

// Owner parties (accounting entry points, owner tables stay in Sales/Purchase)
const Customers = lazy(() => import("./pages/customers/page"));
const CustomerDetail = lazy(() => import("./pages/customers/[id]/page"));
const Suppliers = lazy(() => import("./pages/suppliers/page"));
const SupplierDetail = lazy(() => import("./pages/suppliers/[id]/page"));

// Standalone pages
const ChartOfAccounts = lazy(() => import("./pages/chart-of-accounts/page"));
const Currencies = lazy(() => import("./pages/currencies/page"));
const AssetCategories = lazy(() => import("./pages/asset-categories/page"));
const Depreciation = lazy(() => import("./pages/depreciation/page"));
const Intercompany = lazy(() => import("./pages/intercompany/page"));
const TaxTemplates = lazy(() => import("./pages/tax-templates/page"));
const FiscalYears = lazy(() => import("./pages/fiscal-years/page"));
const SetupChecklist = lazy(() => import("./pages/setup-checklist/page"));
const TaxFilings = lazy(() => import("./pages/tax-filings/page"));
const TaxFilingDetail = lazy(() => import("./pages/tax-filings/[id]/page"));
const FinancialStatementPacks = lazy(() => import("./pages/financial-statement-packs/page"));
const FinancialStatementPackDetail = lazy(() => import("./pages/financial-statement-packs/[id]/page"));
const PaymentMethods = lazy(() => import("./pages/payment-methods/page"));
const PriceLists = lazy(() => import("./pages/price-lists/page"));
const WhtCertificates = lazy(() => import("./pages/wht-certificates/page"));
const WhtCertificateNew = lazy(() => import("./pages/wht-certificates/new/page"));
const WhtCertificateDetail = lazy(() => import("./pages/wht-certificates/[id]/page"));
const WhtTypes = lazy(() => import("./pages/wht-types/page"));
const CostCenters = lazy(() => import("./pages/cost-centers/page"));
// EPIC #412 Phase 3 — admin pages for analytic dimensions.
const DimensionTypes = lazy(() => import("./pages/dimensions/types/page"));
const DimensionValues = lazy(() => import("./pages/dimensions/values/page"));
// EPIC #412 Phase 4 — Distribution Rules.
const DistributionModels = lazy(() => import("./pages/distribution-models/page"));
const DistributionModelNew = lazy(() => import("./pages/distribution-models/new/page"));
const DistributionModelEdit = lazy(() => import("./pages/distribution-models/[id]/page"));
const Items = lazy(() => import("./pages/items/page"));

// Reports
const Reports = lazy(() => import("./pages/reports/page"));
const TrialBalance = lazy(() => import("./pages/reports/trial-balance/page"));
const ProfitLoss = lazy(() => import("./pages/reports/profit-loss/page"));
const CostCenterPL = lazy(() => import("./pages/reports/cost-center-pl/page"));
const PnlByRental = lazy(() => import("./pages/reports/pnl-by-rental/page"));
const PnlByBranch = lazy(() => import("./pages/reports/pnl-by-branch/page"));
const PnlByCustomer = lazy(() => import("./pages/reports/pnl-by-customer/page"));
// EPIC #412 Phase 5 (issue #435) — Pivot Report
const PivotReport = lazy(() => import("./pages/reports/pivot/page"));
const GeneralLedger = lazy(() => import("./pages/reports/general-ledger/page"));
const VatReport = lazy(() => import("./pages/reports/vat/page"));
const Pp36Report = lazy(() => import("./pages/reports/pp36/page"));
const Outstanding = lazy(() => import("./pages/reports/outstanding/page"));
const OutstandingAP = lazy(() => import("./pages/reports/outstanding/ap/page"));
const OutstandingAR = lazy(() => import("./pages/reports/outstanding/ar/page"));
const WhtReport = lazy(() => import("./pages/reports/wht/page"));
const PaymentReconciliation = lazy(() => import("./pages/reports/payment-reconciliation/page"));
const BankReconciliation = lazy(() => import("./pages/reports/bank-reconciliation/page"));
const CashFlow = lazy(() => import("./pages/reports/cash-flow/page"));
const BalanceSheet = lazy(() => import("./pages/reports/balance-sheet/page"));
const BudgetReport = lazy(() => import("./pages/reports/budget/page"));
const AgingReport = lazy(() => import("./pages/reports/aging/page"));

export const accountingRoutes: RouteObject[] = [
  { index: true, Component: AccountingIndex },
  { path: "dashboard", Component: Dashboard },

  // Invoices
  { path: "invoices", Component: Invoices },
  { path: "invoices/new", Component: InvoiceNew },
  { path: "invoices/:id", Component: InvoiceDetail },

  // Payments
  { path: "payments", Component: Payments },
  { path: "payments/:id", Component: PaymentDetail },
  { path: "bank-accounts", Component: BankAccounts },
  { path: "bank-statements", Component: BankStatements },
  { path: "cheques", Component: Cheques },
  { path: "cheques/:id", Component: ChequeDetail },

  // Receipts
  { path: "receipts", Component: Receipts },
  { path: "receipts/:id", Component: ReceiptDetail },

  // Journal Entries
  { path: "journal-entries", Component: JournalEntries },
  { path: "journal-entries/new", Component: JournalEntryNew },
  { path: "journal-entries/:id", Component: JournalEntryDetail },

  // Credit Notes
  { path: "credit-notes", Component: CreditNotes },
  { path: "credit-notes/new", Component: CreditNoteNew },
  { path: "credit-notes/:id", Component: CreditNoteDetail },

  // Debit Notes
  { path: "debit-notes", Component: DebitNotes },
  { path: "debit-notes/new", Component: DebitNoteNew },
  { path: "debit-notes/:id", Component: DebitNoteDetail },

  // Fixed Assets
  { path: "fixed-assets", Component: FixedAssets },
  { path: "fixed-assets/new", Component: FixedAssetNew },
  { path: "fixed-assets/:id", Component: FixedAssetDetail },

  // Budgets
  { path: "budgets", Component: Budgets },
  { path: "budgets/new", Component: BudgetNew },
  { path: "budgets/:id", Component: BudgetDetail },

  // Recurring Templates
  { path: "recurring-templates", Component: RecurringTemplates },
  { path: "recurring-templates/new", Component: RecurringTemplateNew },
  { path: "recurring-templates/:id", Component: RecurringTemplateDetail },

  { path: "customers", Component: Customers },
  { path: "customers/:id", Component: CustomerDetail },
  { path: "suppliers", Component: Suppliers },
  { path: "suppliers/:id", Component: SupplierDetail },

  // Standalone
  { path: "chart-of-accounts", Component: ChartOfAccounts },
  { path: "currencies", Component: Currencies },
  { path: "asset-categories", Component: AssetCategories },
  { path: "depreciation", Component: Depreciation },
  { path: "intercompany", Component: Intercompany },
  { path: "tax-templates", Component: TaxTemplates },
  { path: "fiscal-years", Component: FiscalYears },
  { path: "setup-checklist", Component: SetupChecklist },
  { path: "tax-filings", Component: TaxFilings },
  { path: "tax-filings/:id", Component: TaxFilingDetail },
  { path: "financial-statement-packs", Component: FinancialStatementPacks },
  { path: "financial-statement-packs/:id", Component: FinancialStatementPackDetail },
  { path: "payment-methods", Component: PaymentMethods },
  { path: "price-lists", Component: PriceLists },
  { path: "wht-certificates", Component: WhtCertificates },
  { path: "wht-certificates/new", Component: WhtCertificateNew },
  { path: "wht-certificates/:id", Component: WhtCertificateDetail },
  { path: "wht-types", Component: WhtTypes },
  { path: "cost-centers", Component: CostCenters },
  // EPIC #412 Phase 3 — Analytic Dimensions admin.
  { path: "dimensions/types", Component: DimensionTypes },
  { path: "dimensions/values", Component: DimensionValues },
  // EPIC #412 Phase 4 — Distribution Rules.
  { path: "distribution-models", Component: DistributionModels },
  { path: "distribution-models/new", Component: DistributionModelNew },
  { path: "distribution-models/:id", Component: DistributionModelEdit },
  { path: "items", Component: Items },

  // Reports
  { path: "reports", Component: Reports },
  { path: "reports/trial-balance", Component: TrialBalance },
  { path: "reports/profit-loss", Component: ProfitLoss },
  { path: "reports/cost-center-pl", Component: CostCenterPL },
  { path: "reports/pnl-by-rental", Component: PnlByRental },
  { path: "reports/pnl-by-branch", Component: PnlByBranch },
  { path: "reports/pnl-by-customer", Component: PnlByCustomer },
  // EPIC #412 Phase 5 (issue #435) — Pivot Report
  { path: "reports/pivot", Component: PivotReport },
  { path: "reports/general-ledger", Component: GeneralLedger },
  { path: "reports/vat", Component: VatReport },
  { path: "reports/pp36", Component: Pp36Report },
  { path: "reports/outstanding", Component: Outstanding },
  { path: "reports/outstanding/ap", Component: OutstandingAP },
  { path: "reports/outstanding/ar", Component: OutstandingAR },
  { path: "reports/wht", Component: WhtReport },
  { path: "reports/payment-reconciliation", Component: PaymentReconciliation },
  { path: "reports/bank-reconciliation", Component: BankReconciliation },
  { path: "reports/cash-flow", Component: CashFlow },
  { path: "reports/balance-sheet", Component: BalanceSheet },
  { path: "reports/budget", Component: BudgetReport },
  { path: "reports/aging", Component: AgingReport },
];
